import React, { useState } from 'react';
import OpportunityCard from '../components/OpportunityCard';
import OpportunityModal from '../components/OpportunityModal';
import { AlertIcon, ChartIcon } from '../components/icons/NavigationIcons';

// Mock opportunities data (will be replaced with API call)
const mockOpportunities = [
  {
    id: '1',
    type: 'budget_optimization' as const,
    title: 'Increase Budget for High-Performing Campaign',
    description: 'Campaign "Summer Sale 2026" is performing 45% above target ROAS. Consider increasing budget by $500/day.',
    impact: {
      type: 'revenue_increase' as const,
      amount: 2500,
      period: 'per_month' as const,
    },
    effort: 'low' as const,
    confidence: 0.89,
    affected_items: [
      { type: 'campaign', id: 'camp_123', name: 'Summer Sale 2026' },
    ],
    created_at: '2026-01-27T10:00:00Z',
    status: 'pending' as const,
  },
  {
    id: '2',
    type: 'audience_expansion' as const,
    title: 'Expand to Similar Audiences',
    description: 'Your "Tech Enthusiasts 25-34" audience is saturated. Lookalike audience shows 73% match potential.',
    impact: {
      type: 'revenue_increase' as const,
      amount: 3500,
      period: 'per_month' as const,
    },
    effort: 'medium' as const,
    confidence: 0.82,
    affected_items: [
      { type: 'campaign', id: 'camp_456', name: 'Q1 Product Launch' },
    ],
    created_at: '2026-01-27T09:30:00Z',
    status: 'pending' as const,
  },
  {
    id: '3',
    type: 'creative_refresh' as const,
    title: 'Creative Fatigue Detected',
    description: 'Ad creative #1234 shows 32% CTR decline over 14 days. Time to refresh creative assets.',
    impact: {
      type: 'performance_improvement' as const,
      amount: 800,
      period: 'per_month' as const,
    },
    effort: 'high' as const,
    confidence: 0.75,
    affected_items: [
      { type: 'ad', id: 'ad_1234', name: 'Brand Awareness Ad #1234' },
    ],
    created_at: '2026-01-27T08:15:00Z',
    status: 'pending' as const,
  },
  {
    id: '4',
    type: 'bid_adjustment' as const,
    title: 'Reduce Bids During Low-Performance Hours',
    description: 'Data shows 28% lower conversion rate between 2AM-6AM. Reduce bids by 30% during these hours.',
    impact: {
      type: 'cost_savings' as const,
      amount: 450,
      period: 'per_month' as const,
    },
    effort: 'low' as const,
    confidence: 0.88,
    affected_items: [
      { type: 'campaign', id: 'camp_101', name: 'Always-On Retargeting' },
    ],
    created_at: '2026-01-26T16:45:00Z',
    status: 'pending' as const,
  },
  {
    id: '5',
    type: 'budget_reallocation' as const,
    title: 'Shift Budget from Underperforming Campaign',
    description: 'Campaign "Winter Clearance" is 65% below target ROAS. Consider reallocating $300/day to top performers.',
    impact: {
      type: 'revenue_increase' as const,
      amount: 1800,
      period: 'per_month' as const,
    },
    effort: 'medium' as const,
    confidence: 0.85,
    affected_items: [
      { type: 'campaign', id: 'camp_202', name: 'Winter Clearance' },
    ],
    created_at: '2026-01-26T14:20:00Z',
    status: 'pending' as const,
  },
];

export const OpportunitiesPage: React.FC = () => {
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'applied' | 'dismissed'>('all');

  // Calculate impact level from amount for filtering
  const getImpactLevel = (amount: number): 'high' | 'medium' | 'low' => {
    if (amount >= 2000) return 'high';
    if (amount >= 1000) return 'medium';
    return 'low';
  };

  const filteredOpportunities = mockOpportunities.filter((opp) => {
    const impactLevel = getImpactLevel(opp.impact.amount);
    const impactMatch = filter === 'all' || impactLevel === filter;
    const statusMatch = statusFilter === 'all' || opp.status === statusFilter;
    return impactMatch && statusMatch;
  });

  const stats = {
    total: mockOpportunities.length,
    high: mockOpportunities.filter((o) => getImpactLevel(o.impact.amount) === 'high').length,
    medium: mockOpportunities.filter((o) => getImpactLevel(o.impact.amount) === 'medium').length,
    low: mockOpportunities.filter((o) => getImpactLevel(o.impact.amount) === 'low').length,
    estimatedValue: mockOpportunities.reduce((sum, o) => sum + o.impact.amount, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Opportunities</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          AI-detected optimization opportunities based on your campaign performance data
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Opportunities</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-sungold-100 dark:bg-gp-sungold-900 flex items-center justify-center">
              <AlertIcon className="w-6 h-6 text-gp-sungold-600 dark:text-gp-sungold-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">High Impact</p>
              <p className="text-3xl font-bold text-gp-orange dark:text-gp-orange-400 mt-1">{stats.high}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-orange-100 dark:bg-gp-orange-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-orange" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Medium Impact</p>
              <p className="text-3xl font-bold text-gp-sungold dark:text-gp-sungold-400 mt-1">{stats.medium}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-sungold-100 dark:bg-gp-sungold-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-sungold" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Est. Value</p>
              <p className="text-3xl font-bold text-gp-forest dark:text-gp-forest-400 mt-1">
                ${(stats.estimatedValue / 1000).toFixed(1)}k
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-forest-100 dark:bg-gp-forest-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-forest" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Impact Level
            </label>
            <div className="flex gap-2">
              {['all', 'high', 'medium', 'low'].map((level) => (
                <button
                  key={level}
                  onClick={() => setFilter(level as any)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${
                      filter === level
                        ? 'bg-gp-sungold text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <div className="flex gap-2">
              {['all', 'pending', 'applied', 'dismissed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status as any)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${
                      statusFilter === status
                        ? 'bg-gp-sungold text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Opportunities List */}
      <div className="space-y-4">
        {filteredOpportunities.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center border border-gray-200 dark:border-gray-700">
            <AlertIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No opportunities found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your filters or check back later for new AI-detected opportunities.
            </p>
          </div>
        ) : (
          filteredOpportunities.map((opportunity) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity as any}
              onViewDetails={() => setSelectedOpportunity(opportunity)}
              onDismiss={(id) => console.log('Dismiss:', id)}
            />
          ))
        )}
      </div>

      {/* Opportunity Modal - TODO: Implement full modal */}
      {selectedOpportunity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedOpportunity(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{selectedOpportunity.title}</h2>
            <p className="mb-4">{selectedOpportunity.description}</p>
            <button
              onClick={() => setSelectedOpportunity(null)}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
